import { Button, Menu, MenuItem } from '@mui/material'
import { useRef, useState } from 'react'
import { useCellVisualizationsSlice } from 'redux/slices/hooks/useCellVisualizationsSlice'
import { useNotificationSlice } from 'redux/slices/hooks/useNotificationSlice'
import useLabelingSlice from 'redux/slices/hooks/useLabelingSlice'
import { ConflictMode } from 'redux/slices/LabelingSlice'
import { getColumn } from '../arrow/arqueroHelpers'
import { parseLabelFile } from './labelingFileUtils'

/**
 * IMPORT button with the two conflict-resolution modes, then a file picker.
 * Parses the chosen CSV/TSV, merges it per the selected mode, and reports how
 * many labels were imported and how many conflicts were resolved.
 */
export const LabelImportButton = (): JSX.Element => {
  const {
    labeling: { labels },
    importLabels,
  } = useLabelingSlice()
  const { dataFrame } = useCellVisualizationsSlice()
  const { showSuccess, showError } = useNotificationSlice()

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const modeRef = useRef<ConflictMode>('current-wins')

  const pickFile = (mode: ConflictMode) => {
    modeRef.current = mode
    setAnchorEl(null)
    fileInputRef.current?.click()
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    try {
      const text = await file.text()
      const parsedRows = parseLabelFile(text)
      const mode = modeRef.current

      // Keep only rows whose cell belongs to this session, so labels from a
      // different run/session do not silently accumulate.
      const sessionCellIds = new Set(dataFrame ? getColumn<string>(dataFrame, 'CELL_ID') : [])
      const rows = parsedRows.filter((row) => sessionCellIds.has(row.cell_id))
      const ignored = parsedRows.length - rows.length

      if (rows.length === 0) {
        showError(
          ignored > 0
            ? `None of the ${ignored} rows match cells in this session.`
            : 'No labeled rows found in the file.'
        )
        return
      }

      const conflicts = rows.filter((row) => labels[row.cell_id]).length
      const applied = mode === 'imported-wins' ? rows.length : rows.length - conflicts

      importLabels({
        entries: rows.map((row) => ({
          cellId: row.cell_id,
          label: row.label,
          labeledAt: row.labeled_at,
        })),
        mode,
      })

      const ignoredNote = ignored > 0 ? ` ${ignored} rows ignored (not in this session).` : ''
      showSuccess(`Imported ${applied} labels. ${conflicts} conflicts resolved.${ignoredNote}`)
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Could not import the label file.')
    } finally {
      // Reset so picking the same file again still fires a change event
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        data-testid="import-button"
      >
        Import
      </Button>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={() => pickFile('current-wins')}>
          Keep current session labels on conflict
        </MenuItem>
        <MenuItem onClick={() => pickFile('imported-wins')}>
          Imported labels override on conflict
        </MenuItem>
      </Menu>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.tsv,text/csv,text/tab-separated-values"
        hidden
        onChange={handleFileChange}
      />
    </>
  )
}

export default LabelImportButton
